import { FeatureCylinder3D } from '../procedural/FeatureCylinder3D'
import { PolygonFeatureLabel3D } from '../procedural/PolygonFeatureLabel3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from '../procedural/SurfaceLineFeature3D'
import {
  SurfaceMeshFeature3D,
  SurfaceMeshSelectionType,
} from '../procedural/SurfaceMeshFeature3D'
import { SurfaceFeature3D } from '../SurfaceFeature3D'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import React, { useMemo } from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentPolygon,
  GeckoShipDeckDrain,
} from 'types'

/**
 * component representing a drain on the surface of a ship deck component
 * @param model (the drain model from the asset model)
 * @constructor
 */
export const DeckDrainFeature3D: React.FC<{
  model: GeckoShipDeckDrain
}> = ({ model }) => {
  const { bounds, wrapMode } = useSurfaceProps()

  const { drainCircleRadius, centerGeometry } = React.useMemo(() => {
    // Calculate bounding box dimensions from geometry
    const xCoords = model.polygonalPlate.geometry.map((point) => point.x.num)
    const yCoords = model.polygonalPlate.geometry.map((point) => point.y.num)

    const minX = Math.min(...xCoords)
    const maxX = Math.max(...xCoords)
    const minY = Math.min(...yCoords)
    const maxY = Math.max(...yCoords)

    const width = maxX - minX
    const height = maxY - minY
    const smallerDimension = Math.min(width, height)
    const radius = smallerDimension * 0.4 // 40% of smaller dimension, similar to flush hatch

    // Calculate center position of the bounding box
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2

    // Create geometry for SurfaceFeature3D using the same units as existing geometry
    const firstPoint = model.polygonalPlate.geometry[0]
    const centerGeometry = GeckoComponentCoords.CreateFromValues(
      bounds.componentID,
      centerX,
      firstPoint.x.unit,
      centerY,
      firstPoint.y.unit
    )

    return { drainCircleRadius: radius, centerGeometry }
  }, [model.polygonalPlate.geometry, bounds.componentID])

  const boundingPath = useMemo(
    () =>
      GeckoComponentPolygon.CreateFromPolygonalPlate(
        model.polygonalPlate.geometry,
        bounds,
        wrapMode
      ),
    [bounds, model.polygonalPlate.geometry, wrapMode]
  )

  return (
    <>
      <SurfaceFeature3D
        model={{ id: model.id, type: model.type, geometry: centerGeometry }}>
        <FeatureCylinder3D
          feature={model}
          position={[0, 0, 0]}
          radius={drainCircleRadius}
          height={0.1}
          debugColor={'pink'}
        />
      </SurfaceFeature3D>
      <SurfaceLineFeature3D
        path={boundingPath}
        featureModel={model}
        lineSelectionType={SurfaceLineSelectionType.SelectableFeatureLine}
      />
      <SurfaceMeshFeature3D
        path={boundingPath}
        featureModel={model}
        selectionType={SurfaceMeshSelectionType.AssetModelEditorSelectable}
        userDataName={'drain'}
      />
      <PolygonFeatureLabel3D feature={model} polygonPath={boundingPath} />
    </>
  )
}
